/**
 * Atlas Backend API Client
 *
 * This service communicates with the Atlas backend API for:
 * - Cached market data (reduces Hyperliquid rate limits)
 * - Smart order routing calculations
 * - Market metadata (logos, decimals, etc.)
 */

import type { Hip3Market } from '../types';

const API_BASE_URL = import.meta.env.VITE_ATLAS_API_URL || 'https://atlas-xyz-ddd9750d15fe.herokuapp.com';

export interface BackendMarketData {
  baseAsset: string;
  venues: BackendVenueData[];
  aggregatedStats: AggregatedStats;
}

export interface BackendVenueData {
  provider: string;
  symbol: string;
  dexIndex?: string;
  maxLeverage: number;
  szDecimals: number;
  onlyIsolated: boolean;
}

export interface AggregatedStats {
  markPrice: number;
  oraclePrice?: number;
  volume24h: number;
  openInterest: number;
  funding8h: number;
  change24h: number;
}

export interface MarketsResponse {
  markets: BackendMarketData[];
  lastUpdated: string;
}

export interface MarketMetadata {
  [baseAsset: string]: {
    logoUrl: string;
    szDecimals: number;
    maxLeverage: number;
    onlyIsolated: boolean;
    venues: string[];
  };
}

export interface SmartRouteVenue {
  provider: string;
  size: number;
  price: number;
  fee: number;
  percentage: number;
}

export interface SmartRouteResponse {
  route: {
    venues: SmartRouteVenue[];
    averagePrice: number;
    totalFee: number;
    estimatedSlippage: number;
    expiry: string;
  };
}

export interface ValidateCodeResponse {
  valid: boolean;
  message?: string;
}

export interface CheckAccessResponse {
  hasAccess: boolean;
}

class AtlasApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Fetch all markets with aggregated data from the backend cache
   * This replaces direct calls to Hyperliquid's metaAndAssetCtxs
   */
  async getMarkets(): Promise<MarketsResponse> {
    const response = await fetch(`${this.baseUrl}/api/markets`);

    if (!response.ok) {
      throw new Error(`Failed to fetch markets: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Fetch market metadata (logos, decimals, etc.)
   * This data is aggressively cached as it rarely changes
   */
  async getMarketMetadata(): Promise<MarketMetadata> {
    const response = await fetch(`${this.baseUrl}/api/markets/metadata`);

    if (!response.ok) {
      throw new Error(`Failed to fetch market metadata: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Calculate smart routing for an order
   * This moves the routing algorithm to the backend (protects IP)
   */
  async getSmartRoute(
    baseAsset: string,
    side: 'buy' | 'sell',
    size: number,
    orderType: 'market' | 'limit'
  ): Promise<SmartRouteResponse> {
    const response = await fetch(`${this.baseUrl}/api/routing/quote`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        baseAsset,
        side,
        size,
        orderType,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to get smart route: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Health check endpoint
   */
  async healthCheck(): Promise<{ status: string }> {
    const response = await fetch(`${this.baseUrl}/health`);

    if (!response.ok) {
      throw new Error(`Health check failed: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Convert backend market data to Hip3Market format used by the frontend
   */
  convertToHip3Markets(backendData: BackendMarketData[]): Hip3Market[] {
    const markets: Hip3Market[] = [];

    for (const marketData of backendData) {
      for (const venue of marketData.venues) {
        markets.push({
          id: venue.symbol,
          symbol: venue.symbol,
          name: `${marketData.baseAsset} Perpetual`,
          baseAsset: marketData.baseAsset,
          quoteAsset: 'USDC',
          provider: venue.provider,
          dexIndex: venue.dexIndex,
          isNative: !venue.dexIndex,
          maxLeverage: venue.maxLeverage,
          szDecimals: venue.szDecimals,
          onlyIsolated: venue.onlyIsolated,
        });
      }
    }

    return markets;
  }

  /**
   * Check if the backend is available
   */
  async isAvailable(): Promise<boolean> {
    try {
      await this.healthCheck();
      return true;
    } catch (error) {
      console.error('Backend is not available:', error);
      return false;
    }
  }

  /**
   * Validate an invite code
   * POST /api/auth/validate-code
   */
  async validateCode(code: string, walletAddress?: string): Promise<ValidateCodeResponse> {
    const response = await fetch(`${this.baseUrl}/api/auth/validate-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        code,
        wallet_address: walletAddress,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to validate code: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Check if a wallet address has access
   * GET /api/auth/check-access
   */
  async checkAccess(walletAddress: string): Promise<CheckAccessResponse> {
    const response = await fetch(
      `${this.baseUrl}/api/auth/check-access?wallet_address=${encodeURIComponent(walletAddress)}`
    );

    if (!response.ok) {
      throw new Error(`Failed to check access: ${response.statusText}`);
    }

    return response.json();
  }
}

export const atlasApi = new AtlasApiClient();
